import React from 'react';
import { connect } from 'react-redux';
import { merge, pipe, curry } from 'ramda';
import classnames from 'classnames';
import { Button, Glyphicon } from 'react-bootstrap';
import { bindMethods } from '../../utils/core';
import exportToCsv from '../../utils/exportToCsv';
import { getStatesByFlow } from '../../sources/dashboard';
import GenericSelectBox from '../generic/GenericSelectBox';
import { getTimeRange } from '../../utils/dashboard';
import Table from '../Table';
import Pager from '../Pager';
import { stateStatus, timeFilter } from '../../constants';
import ClickSelectText from '../ClickSelectText';
import moment from 'moment';
import { fetchAllPaged } from '../../utils/ajax';

const initialState = {
    isLoading: false,
    isFetchingAllStates: false,
    items: [],
    paging: {
        page: 1,
        pageSize: 50,
        total: 0,
    },
    filter: {
        status: stateStatus.IN_PROGRESS,
        dates: {
            from: null,
            to: null,
            groupBy: 'monthly',
        },
    },
};

const { ERRORED, EXPIRED, DONE, IN_PROGRESS } = stateStatus;

const exportToCsvCurried = curry(exportToCsv);

export const getStatusText = ({ status, item }) => {

    const { isDone, isExpired } = item;

    switch (status) {

    case DONE:
        return 'Done';

    case IN_PROGRESS:
        return 'In Progress';

    case EXPIRED:
        return 'Expired';

    case ERRORED:
        return isDone
            ? 'Done'
            : isExpired
                ? 'Expired'
                : 'In Progress';

    // All
    default:
        return isDone
            ? 'Done'
            : 'In Progress';
    }
};

export const showErrorFlag = ({ status, item }) => {

    if (!item.hasRootFaults || status === ERRORED) {
        return false;
    }

    return true;
};

export const showExpiredFlag = ({ status, item }) => {

    if (!item.isExpired || status === EXPIRED) {
        return false;
    }

    return true;
};

export const statusClassName = ({ isDone, isExpired, hasRootFaults }) => classnames({
    'dashboard-status-column': true,
    'status-errored': hasRootFaults,
    'status-expired': isExpired && !hasRootFaults,
    'status-done': isDone && !hasRootFaults && !isExpired,
});

export const formatStateDataToCsv = (states) => {

    const headerRow = [
        'Done',
        'Expired',
        'Has Root Faults',
        'Current Step',
        'User',
        'Date Created',
        'Last Action On',
        'Join Uri',
        'State ID',
    ];

    const bodyRows = states.map(
        state => [
            state.isDone,
            state.isExpired,
            state.hasRootFaults,
            state.currentMapElementDeveloperName,
            state.currentRunningUserEmail ? state.currentRunningUserEmail : 'Pubilc User',
            state.dateCreated,
            state.dateModified,
            state.joinUri,
            state.id,
        ],
    );

    const allRows = [headerRow, ...bodyRows];

    return allRows;
};

/**
 * @param {Object} selectedFlow Object containing information about the flow which the view should be based on
 * @param {Function} onServiceInvokerClick Called when clicking through to a state's service invoker communication. Passed the selected state item data.
 * @description Controls and shows information about a single flow such as a list of states
 */
export class StateList extends React.Component {

    constructor() {
        super();

        this.state = initialState;

        bindMethods(
            this,
            'fetchData',
            'onStatusFilterChange',
            'exportResultsToCSV',
            'onTimeFilterChange',
            'onRefresh',
        );
    }

    componentDidMount() {
        this.fetchData(1);
    }

    onRefresh() {
        this.fetchData(1);
    }

    onStatusFilterChange({ target }) {

        const updatedFilter = merge(this.state.filter, { status: target.value });

        this.setState(
            {
                filter: updatedFilter,
            },
            () => this.fetchData(),
        );
    }

    onTimeFilterChange({ target }) {

        const updatedFilter = merge(this.state.filter, { dates: getTimeRange(target) });

        this.setState(
            {
                filter: updatedFilter,
            },
            () => this.fetchData(),
        );
    }

    fetchData(page = 1) {

        const { status } = this.state.filter;
        const { from, to } = this.state.filter.dates;

        const urlParams = {
            status,
            page,
            pageSize: 50,
        };

        if (from !== null && to !== null) {
            urlParams['from'] = from;
            urlParams['to'] = to;
        }

        this.setState({ isLoading: true });

        getStatesByFlow({
            token: this.props.authenticationToken,
            tenantId: this.props.tenantId,
            flowId: this.props.selectedFlow.id,
            params: urlParams,
        }).then(
            ({ _meta: paging, items }) => this.setState({
                isLoading: false,
                items,
                paging,
            }),
        ).catch(() => {
            this.setState({
                isLoading: false,
                items: initialState.items,
                paging: initialState.paging,
            });
        });
    }

    async exportResultsToCSV() {

        this.setState({ isFetchingAllStates: true });

        const states = await fetchAllPaged(getStatesByFlow, {
            token: this.props.authenticationToken,
            tenantId: this.props.tenantId,
            flowId: this.props.selectedFlow.id,
            params: {
                status: this.state.filter.status,
                from: this.state.filter.dates.from,
                to: this.state.filter.dates.to,
                pageSize: 1000,
            },
        }, 1000);

        pipe(
            formatStateDataToCsv,
            exportToCsvCurried('states.csv'),
        )(states);

        this.setState({ isFetchingAllStates: false });
    }

    render() {

        const columns = [
            {
                renderHeader: () => 'Status',
                renderCell: ({ item }) => {

                    const { status } = this.state.filter;

                    const text = getStatusText({ status, item });
                    const showError = showErrorFlag({ status, item });
                    const showExpired = showExpiredFlag({ status, item });
                    const className = statusClassName(item);

                    return (
                        <span className={className}>
                            { text }
                            { showExpired && <span className="glyphicon glyphicon-time" title="Expired" /> }
                            { showError && <span className="glyphicon glyphicon-exclamation-sign status-errored" title="Root Faults" /> }
                        </span>
                    );
                },
            },
            {
                renderHeader: () => 'Current Step',
                renderCell: ({ item }) => item.currentMapElementDeveloperName,
            },
            {
                renderHeader: () => 'User',
                renderCell: ({ item }) => {
                    const hasUser = item.currentRunningUserEmail !== null;
                    return hasUser ? item.currentRunningUserEmail : <i>Public User</i>;
                },
            },
            {
                renderHeader: () => 'Elapsed Time',
                renderCell: ({ item }) => moment(item.dateCreated).fromNow(true),
            },
            {
                renderHeader: () => 'Created (UTC)',
                renderCell: ({ item }) => moment(item.dateCreated).utc().format('MMM Do YYYY, HH:mm:ss'),
            },
            {
                renderHeader: () => 'Last Action (UTC)',
                renderCell: ({ item }) => moment(item.dateModified).utc().format('MMM Do YYYY, HH:mm:ss'),
            },
            {
                renderHeader: () => 'Join Uri',
                renderCell: ({ item }) => <a href={item.joinUri}>{ item.joinUri }</a>,
            },
            {
                renderHeader: () => 'State ID',
                renderCell: ({ item }) => <ClickSelectText text={item.id} />,
            },
            {
                renderHeader: () => 'Actions',
                renderCell: ({ item }) => (
                    <button className="link-emulate" onClick={() => this.props.onServiceInvokerClick(item)}>
                        Log
                    </button>
                ),
            },
        ];

        const items = this.state.isLoading ? [] : this.state.items;

        return (
            <div className="dashboard-states">

                <h1>
                    {`${this.props.selectedFlow.developerName} Flow`}
                    <span className="flow-id">
                        {this.props.selectedFlow.id}
                    </span>
                </h1>

                <div className="state-actions">
                    <GenericSelectBox onFilter={this.onTimeFilterChange} selected={timeFilter.LAST_YEAR}>
                        <option value={timeFilter.LAST_YEAR}>Last Year</option>
                        <option value={timeFilter.LAST_30_DAYS}>Last 30 Days</option>
                        <option value={timeFilter.LAST_7_DAYS}>Last 7 Days</option>
                        <option value={timeFilter.LAST_24_HOURS}>Last 24 Hours</option>
                    </GenericSelectBox>

                    <GenericSelectBox onFilter={this.onStatusFilterChange} selected={stateStatus.IN_PROGRESS}>
                        <option value={stateStatus.IN_PROGRESS}>In Progress</option>
                        <option value={stateStatus.DONE}>Done</option>
                        <option value={stateStatus.ERRORED}>Errored</option>
                        <option value={stateStatus.EXPIRED}>Expired</option>
                        <option>All</option>
                    </GenericSelectBox>

                    <div className="form-group export-csv">
                        <Button
                            title="Download results in CSV format"
                            className="export"
                            disabled={items.length === 0}
                            onClick={this.exportResultsToCSV}
                        >
                            <Glyphicon glyph="download-alt" />
                        </Button>
                    </div>

                    <Button
                        title="Refresh results"
                        className="btn-default"
                        onClick={this.onRefresh}
                    >
                        <Glyphicon glyph="refresh" />
                    </Button>
                </div>

                <Table className="table" columns={columns} items={items} isLoading={this.state.isLoading} />

                <Pager {...this.state.paging} onPageClick={this.fetchData} />

                {
                    React.createElement(
                        manywho.component.getByName('wait'),
                        {
                            isVisible: this.state.isFetchingAllStates,
                            message: 'Downloading state data',
                        },
                        null,
                    )
                }

            </div>
        );

    }

}

export default connect(
    ({ currentUser: { authenticationToken, tenantId } }) => ({ authenticationToken, tenantId }),
)(StateList);
